// @flow
import moment from 'moment';
import _ from 'lodash';

import type { TRound } from '../../modules/types';
import type { TPLeague } from "../../modules/types/league";
import { getActualRound } from '../rounds';

export type TLeague = Object;
export type TLeagueDraftState = 'complete' | 'active' | 'future' | 'unscheduled';

export const isDraftComplete = (league: TLeague) => league.draft_status === "complete";

export const isDraftActive = (league: TLeague) => league.draft_status === "active";

export const isDraftFuture = (league: TLeague) =>
	league.draft_status === "scheduled" &&
	moment().isBefore(league.draft_start);

export const isDraftUnscheduled = (league: TLeague) =>
	league.draft_status === "scheduled" &&
	moment().isBefore(league.draft_start) === false;

export const getDraftState = (league: TLeague): ?TLeagueDraftState => {
	if (isDraftComplete(league)) {
		return 'complete';
	}
	else if (isDraftActive(league)) {
		return 'active';
	}
	else if (isDraftFuture(league)) {
		return 'future';
	}
	else if (isDraftUnscheduled(league)) {
		return 'unscheduled';
	}

	return null;
};

export const getDefaultLeagueRoute = (league: TLeague) => {
	const league_draft_state = getDraftState(league);

	if (!(league && league.id)) return null;

	const states = {
		active: `/league/${league.id}/team/live-draft/`,
		complete: `/league/${league.id}/`,
		future: `/league/${league.id}/team/pre-draft/`,
		unscheduled: `/league/${league.id}/team/pre-draft/`
	};

 	return league_draft_state ? states[league_draft_state] : null;
};

export const getSquadSizeArray = (league: TLeague = {}) => {
	const { squad_size } = league;

	if (!squad_size) {
		return [];
	}

	return _.map(squad_size.split(/[-/]/), _.unary(parseInt));
};

export const getSquadSize = (league: TLeague) => {
	const squad_size = getSquadSizeArray(league);

	if (_.isEmpty(squad_size)) {
		return 0;
	}

	return squad_size.reduce((sum, num) => sum + num, 0);
};

export const delay = (ms: number): any => new Promise((res: any) => setTimeout(res, ms));

export const transactionPlayerName = (transaction: Object, player: Object) => {
	return (`${player[transaction.player_id].first_name} ${player[transaction.player_id].last_name}`).toLowerCase(); // eslint-disable-line max-len
};

/**
 * Gets the rounds between the given league's start and end rounds (inclusive)
 * Also sets any future rounds to be disabled.
 * If league is not given, returns all rounds, with future rounds disabled
 *
 * @param {TRound[]} rounds_ordered List of all rounds
 * @param {TPLeague} league The league to get rounds for (optional)
 */
export const getLeagueRounds = (rounds_ordered: TRound[], league?: TPLeague, ) => {
	const start_round = _.get(league, 'start_round', 1);
	const final_round = _.get(_.last(rounds_ordered), 'id', 1);
	const end_round = _.get(league, 'end_round', final_round);

	if (rounds_ordered && start_round && end_round) {
		const actual_round = getActualRound(rounds_ordered);
		return _(rounds_ordered)
			.filter(({ id }) => _.inRange(id, start_round, end_round + 1))
			.map(round => ({ ...round, disabled: round.id > actual_round.id }))
			.value();
	}

	return rounds_ordered;
};

const FINALS_NAMES = [ "GF", "PF", "SF", "QF & EF" ];
const FINALS_FULL_NAMES = [
	"Grand Final", "Preliminary Finals", "Semi Finals", "Qualifying & Elimination Finals"
];
const FINALS_GAMES_COUNT = [ 1, 2, 2, 4 ];

const FINALS_LENGTHS = {
	'8': 4,
	'6': 3,
	'4': 2,
	'2': 1
};

export const isSecondaryFinals = ({ finals_format, size }: TPLeague) => size >= finals_format * 2;
export const getNumFinalsRounds = ({ finals_format }: TPLeague) => FINALS_LENGTHS[finals_format];
export const getLastRoundBeforeFinals = (league: TPLeague) => {
	const num_finals_rounds = getNumFinalsRounds(league);
	return league.end_round - num_finals_rounds;
};

export const getFinalsRounds = (league: TPLeague, rounds_ordered: TRound[]) => {
	const { finals, finals_format } = league;
	const league_rounds = getLeagueRounds(rounds_ordered, league);

	if (finals) {
		return _.chain(league_rounds)
			.reverse()
			.slice(0, FINALS_LENGTHS[finals_format])
			.map((round, i) => round.id)
			.value();
	}

	return [];
};

export const getLeagueRoundAliases = (league: TPLeague, rounds_ordered: TRound[]) => {
	const { finals, finals_format } = league;
	const league_rounds = getLeagueRounds(rounds_ordered, league);
	const finals_rounds = {};
	if (finals) {
		_(league_rounds)
			.reverse()
			.slice(0, FINALS_LENGTHS[finals_format])
			.forEach((round, i) => finals_rounds[round.id] = FINALS_NAMES[i]);
	}
	return finals_rounds;
};

export const getLeagueFinalFullNames = (league: TPLeague, rounds_ordered: TRound[]) => {
	const { finals, finals_format } = league;
	const league_rounds = getLeagueRounds(rounds_ordered, league);
	const finals_rounds = {};
	if (finals) {
		_(league_rounds)
			.reverse()
			.slice(0, FINALS_LENGTHS[finals_format])
			.forEach((round, i) => finals_rounds[round.id] = FINALS_FULL_NAMES[i]);
	}
	return finals_rounds;
};

export const getLeagueFinalsRoundsGamesCount = (
	league: TPLeague, rounds_ordered: TRound[]
): { [id: number]: number } => {
	const { finals, finals_format } = league;
	const league_rounds = getLeagueRounds(rounds_ordered, league);
	const finals_rounds = {};
	if (finals) {
		_(league_rounds)
			.reverse()
			.slice(0, FINALS_LENGTHS[finals_format])
			.forEach((round, i) => finals_rounds[round.id] = FINALS_GAMES_COUNT[i]);
	}
	return finals_rounds;
};

const arrayToObject = (
	array: Array<Object>,
	propName: string,
	initialObject: Object,
) => {
	if (Array.isArray(array) === false) {
		throw new
		Error('arrayToObject requires a valid array to be passed in as the first parameter');
	}
	if ((propName && typeof propName === 'string') === false) {
		throw new
		Error('arrayToObject requires a propName param of type str to be passed in as 2nd param');
	}
	return array.reduce((obj, item) => {
		obj[item[propName]] = item;
		return obj;
	}, { ...initialObject });
};

export const arrayToObjectById = (
	array: Array<Object>,
	initialObject: Object,
)=> arrayToObject(array, 'id', initialObject);


// WEBPACK FOOTER //
// ./src/helpers/league/index.js